import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GaugeIcon as Gauge, DownloadIcon as Download, WarningCircleIcon as WarningCircle } from "@phosphor-icons/react";
import { meterLabelIt } from "@/lib/meterLabels";

// Mostra solo i 4 toner principali (nero, ciano, magenta, giallo); nasconde drum/fuser/vaschetta/altro.
const isToner = (s) => s.supply_type === "toner" && ["black", "cyan", "magenta", "yellow"].includes(s.color);

const errMsg = (e) => { const d = e?.response?.data?.detail; return (d && typeof d === "object") ? (d.message || d.category) : (d || "Errore"); };
const CAT = { mono: "Mono", colore: "Colore", totale: "Totale", altro: "Altro" };
const fmtNum = (n) => (n == null ? "—" : Number(n).toLocaleString("it-IT"));
const fmtDt = (d) => (d ? new Date(String(d).replace(" ", "T")).toLocaleDateString("it-IT") : "—");

export function PrintanistaMeterDialog({ open, onOpenChange, device }) {
  const [summary, setSummary] = useState(null);
  const [readings, setReadings] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [supplies, setSupplies] = useState([]);
  const [supAlerts, setSupAlerts] = useState([]);
  const [replacements, setReplacements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [catFilter, setCatFilter] = useState("all");

  const load = () => {
    if (!device) return;
    setLoading(true);
    Promise.all([
      api.get(`/devices/${device.id}/meter-summary`),
      api.get(`/devices/${device.id}/meter-readings`),
      api.get(`/devices/${device.id}/assignments`),
      api.get(`/integrations/printanista/meter-anomalies`),
      api.get(`/devices/${device.id}/supplies`),
      api.get(`/devices/${device.id}/supply-alerts`),
      api.get(`/devices/${device.id}/supplies/replacements`),
    ]).then(([s, r, a, an, sup, sa, rp]) => {
      setSummary(s.data); setReadings(r.data.readings || []); setAssignments(a.data.assignments || []);
      setAnomalies((an.data.anomalies || []).filter((x) => x.device_id === device.id));
      setSupplies(sup.data.supplies || []); setSupAlerts(sa.data.alerts || []); setReplacements(rp.data.replacements || []);
    }).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, device]);

  const importHistory = async () => {
    setImporting(true);
    const tid = toast.loading("Importazione storico contatori…");
    try {
      const { data } = await api.post(`/integrations/printanista/devices/${device.id}/meter-history/import`);
      if (data.running) toast.warning("Sincronizzazione già in corso", { id: tid });
      else { const s = data.summary || {}; toast.success("Storico importato", { id: tid, description: `Letture ${s.new} · Scan ignorate ${s.scans_ignored} · Anomalie ${s.anomalies}` }); }
      load();
    } catch (e) { toast.error(errMsg(e), { id: tid }); } finally { setImporting(false); }
  };

  const filteredReads = readings.filter((r) => catFilter === "all" || r.meter_category === catFilter);
  const meters = summary?.meters || [];
  const meterByLabel = (lbl) => meters.find((m) => (meterLabelIt(m.remote_meter_code) || m.meter_name) === lbl);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="prt-meter-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Gauge size={18} className="text-orange-600" /> Contatori · {device?.marca} {device?.modello}</DialogTitle>
          <DialogDescription>Storico contatori Printanista (le scansioni non vengono conteggiate).</DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex flex-wrap gap-2 text-xs">
            {summary?.latest && Object.entries(summary.latest).map(([cat, m]) => {
              const label = ({ mono: "Totale pagine mono", colore: "Totale pagine colore" })[cat] || CAT[cat] || cat;
              const override = (cat === "mono" || cat === "colore") ? meterByLabel(label) : null;
              const value = override ? override.value : m.value;
              return (
                <Badge key={cat} variant="outline" className="rounded-sm" data-testid={`prt-meter-latest-${cat}`}>{label}: {fmtNum(value)}</Badge>
              );
            })}
            {summary?.open_anomalies > 0 && <Badge variant="outline" className="rounded-sm bg-red-100 text-red-800 border-red-200">Anomalie: {summary.open_anomalies}</Badge>}
          </div>
          <Button variant="outline" size="sm" onClick={importHistory} disabled={importing} className="rounded-sm gap-1.5" data-testid="prt-import-history-btn"><Download size={14} /> {importing ? "Import…" : "Importa storico"}</Button>
        </div>

        <Tabs defaultValue="contatori">
          <TabsList className="rounded-sm">
            <TabsTrigger value="contatori" className="rounded-sm">Contatori</TabsTrigger>
            <TabsTrigger value="storico" className="rounded-sm">Storico</TabsTrigger>
            <TabsTrigger value="assegnazioni" className="rounded-sm">Assegnazioni</TabsTrigger>
            <TabsTrigger value="consumabili" className="rounded-sm" data-testid="prt-supplies-tab">Consumabili{supplies.filter(isToner).length ? ` (${supplies.filter(isToner).length})` : ""}</TabsTrigger>
            <TabsTrigger value="anomalie" className="rounded-sm" data-testid="prt-meter-anomalie-tab">Anomalie{anomalies.length ? ` (${anomalies.length})` : ""}</TabsTrigger>
          </TabsList>

          <TabsContent value="contatori" className="pt-3">
            <div className="border border-border rounded-sm divide-y divide-border" data-testid="prt-meter-list">
              {meters.map((m) => (
                <div key={m.remote_meter_code} className="flex items-center justify-between px-3 py-2 text-sm">
                  <div><span className="font-medium">{meterLabelIt(m.remote_meter_code) || m.meter_name}</span> <Badge variant="outline" className="rounded-sm ml-1 text-[10px]">{CAT[m.meter_category] || m.meter_category}</Badge></div>
                  <div className="tabular-nums">{fmtNum(m.value)} <span className="text-xs text-muted-foreground">({fmtDt(m.reading_at)})</span></div>
                </div>
              ))}
              {meters.length === 0 && <div className="px-3 py-6 text-center text-sm text-muted-foreground">Nessun contatore. Usa "Importa storico".</div>}
            </div>
          </TabsContent>

          <TabsContent value="storico" className="pt-3 space-y-2">
            <Select value={catFilter} onValueChange={setCatFilter}>
              <SelectTrigger className="w-44 rounded-sm h-8"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutte le categorie</SelectItem>{Object.entries(CAT).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
            <div className="border border-border rounded-sm overflow-x-auto max-h-[50vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary/40 text-muted-foreground sticky top-0"><tr className="text-left"><th className="py-2 px-2">Data</th><th className="py-2 px-2">Contatore</th><th className="py-2 px-2">Categoria</th><th className="py-2 px-2 text-right">Valore</th><th className="py-2 px-2">Cliente</th><th className="py-2 px-2">Stato</th></tr></thead>
                <tbody>
                  {filteredReads.map((r) => (
                    <tr key={r.id} className="border-t border-border/60">
                      <td className="py-1.5 px-2">{fmtDt(r.reading_at)}</td>
                      <td className="py-1.5 px-2">{meterLabelIt(r.remote_meter_code || r.remote_meter_name)}</td>
                      <td className="py-1.5 px-2">{CAT[r.meter_category] || r.meter_category}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums">{fmtNum(r.value)}</td>
                      <td className="py-1.5 px-2 text-xs text-muted-foreground">{r.cliente_id_at_reading ? "assegnato" : "—"}</td>
                      <td className="py-1.5 px-2">{r.is_anomalous ? <Badge variant="outline" className="rounded-sm text-[10px] bg-red-100 text-red-800 border-red-200">Anomalia</Badge> : <span className="text-muted-foreground text-xs">ok</span>}</td>
                    </tr>
                  ))}
                  {filteredReads.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">Nessuna lettura.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="assegnazioni" className="pt-3">
            <div className="border border-border rounded-sm divide-y divide-border">
              {assignments.map((a) => (
                <div key={a.id} className="px-3 py-2.5 text-sm flex items-center justify-between">
                  <div><span className="font-medium">{a.cliente_nome || "—"}</span>
                    <div className="text-xs text-muted-foreground">Dal {fmtDt(a.started_at)}{a.ended_at ? ` al ${fmtDt(a.ended_at)}` : ""}</div></div>
                  <Badge variant="outline" className={`rounded-sm ${a.status === "active" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-zinc-100 text-zinc-600 border-zinc-200"}`}>{a.status === "active" ? "Attiva" : "Dismessa"}</Badge>
                </div>
              ))}
              {assignments.length === 0 && <div className="px-3 py-6 text-center text-muted-foreground text-sm">Nessuna assegnazione.</div>}
            </div>
          </TabsContent>

          <TabsContent value="consumabili" className="pt-3 space-y-3" data-testid="prt-supplies-view">
            <div className="border border-border rounded-sm divide-y divide-border">
              {supplies.filter(isToner).map((s) => {
                const LV = { critico: "bg-red-100 text-red-800 border-red-200", in_esaurimento: "bg-orange-100 text-orange-800 border-orange-200", esaurimento_previsto: "bg-yellow-100 text-yellow-800 border-yellow-200", attenzione: "bg-amber-100 text-amber-800 border-amber-200", regolare: "bg-emerald-100 text-emerald-800 border-emerald-200", sconosciuto: "bg-zinc-100 text-zinc-600 border-zinc-200" };
                return (
                  <div key={s.slot_key} className="px-3 py-2.5 flex items-center justify-between gap-2 text-sm" data-testid={`prt-supply-${s.slot_key}`}>
                    <div><span className="font-medium">{s.supply_name || s.supply_type}</span>{s.color && <Badge variant="outline" className="rounded-sm ml-1 text-[10px]">{s.color}</Badge>}
                      <div className="text-xs text-muted-foreground">{s.remaining_value != null ? `${s.remaining_value}${s.remaining_unit === "percent" ? "%" : ""}` : "Livello non disponibile"}{s.estimated_empty_date ? ` · esaurimento ${fmtDt(s.estimated_empty_date)}` : ""}{s.remote_status ? ` · ${s.remote_status}` : ""}</div></div>
                    <Badge variant="outline" className={`rounded-sm ${LV[s.active_alert_level] || LV.sconosciuto}`}>{(s.active_alert_level || "").replace("_", " ")}</Badge>
                  </div>
                );
              })}
              {supplies.filter(isToner).length === 0 && <div className="px-3 py-6 text-center text-sm text-muted-foreground">Nessun toner. Usa "Sincronizza contatori" (sincronizza anche i consumabili).</div>}
            </div>
            {supAlerts.filter((a) => isToner(a) && ["open", "acknowledged"].includes(a.status)).length > 0 && (
              <div><div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Alert consumabili</div>
                <div className="border border-border rounded-sm divide-y divide-border" data-testid="prt-supply-alerts-list">
                  {supAlerts.filter((a) => isToner(a) && ["open", "acknowledged"].includes(a.status)).map((a) => (
                    <div key={a.id} className="px-3 py-2 flex items-center justify-between gap-2 text-sm">
                      <div className="flex items-center gap-2"><WarningCircle size={15} className="text-red-600" /><span>{a.supply_name || a.supply_type} {a.color || ""} · {a.severity}</span></div>
                      <div className="flex gap-1.5">
                        <Button variant="ghost" size="sm" className="h-7 rounded-sm text-xs" onClick={() => resolveAlert(a, "acknowledged")}>Presa in carico</Button>
                        <Button variant="ghost" size="sm" className="h-7 rounded-sm text-xs" onClick={() => resolveAlert(a, "resolved_manual")}>Risolvi</Button>
                        <Button variant="ghost" size="sm" className="h-7 rounded-sm text-xs" onClick={() => resolveAlert(a, "ignored")}>Ignora</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {replacements.length > 0 && (
              <div><div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Sostituzioni</div>
                <div className="border border-border rounded-sm divide-y divide-border">
                  {replacements.map((r) => (
                    <div key={r.id} className="px-3 py-2 text-sm flex items-center justify-between"><span>{fmtDt(r.observed_at)}</span><Badge variant="outline" className="rounded-sm text-[10px]">{r.replacement_detection === "inferred" ? "Rilevata automaticamente" : r.replacement_detection === "manual" ? "Manuale" : "Rilevata (remota)"}</Badge></div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="anomalie" className="pt-3">            <div className="border border-border rounded-sm divide-y divide-border" data-testid="prt-meter-anomalies-list">
              {anomalies.map((an) => (
                <div key={an.id} className="px-3 py-2.5 text-sm flex items-center justify-between gap-2">
                  <div className="flex items-start gap-2"><WarningCircle size={15} className="text-red-600 mt-0.5" /><div>{an.message}<div className="text-xs text-muted-foreground">{meterLabelIt(an.remote_meter_code)} · {fmtDt(an.reading_at)}</div></div></div>
                  <div className="flex items-center gap-1.5">
                    <Badge variant="outline" className="rounded-sm text-[10px]">{an.status}</Badge>
                    {an.status === "open" && <>
                      <Button variant="ghost" size="sm" className="h-7 rounded-sm text-xs" onClick={() => resolve(an, "resolved")}>Verificata</Button>
                      <Button variant="ghost" size="sm" className="h-7 rounded-sm text-xs" onClick={() => resolve(an, "ignored")}>Ignora</Button>
                    </>}
                  </div>
                </div>
              ))}
              {anomalies.length === 0 && <div className="px-3 py-6 text-center text-muted-foreground text-sm">Nessuna anomalia.</div>}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );

  async function resolve(an, status) {
    try { await api.put(`/integrations/printanista/meter-anomalies/${an.id}`, { status }); toast.success("Anomalia aggiornata"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  }

  async function resolveAlert(a, status) {
    try { await api.put(`/integrations/printanista/supply-alerts/${a.id}`, { status }); toast.success("Alert aggiornato"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  }
}
